import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, fmtUsd, fmtNum } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import { Button } from "../components/ui/button";
import { Users, Video, Radio, BadgeCheck, Lock, Mic } from "lucide-react";
import { toast } from "sonner";

export default function StreamerProfile() {
  const { id } = useParams();
  const [streamer, setStreamer] = useState(null);
  const [paying, setPaying] = useState(null);
  const [fee, setFee] = useState(10);
  const [message, setMessage] = useState("");
  const [queue, setQueue] = useState([]);
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/streamers/${id}`).then(({ data }) => setStreamer(data)).catch(() => setStreamer(false));
    api.get("/config/requirements").then(({ data }) => setFee(data.platform_fee_percent)).catch(() => {});
    const loadQueue = () => api.get(`/streamers/${id}/queue`).then(({ data }) => setQueue(data)).catch(() => {});
    loadQueue();
    const iv = setInterval(loadQueue, 15000);
    return () => clearInterval(iv);
  }, [id]);

  const support = async (tier) => {
    if (!user || user === false) {
      toast.info(t("profile_login_first"));
      navigate("/auth");
      return;
    }
    setPaying(tier.id);
    try {
      const { data } = await api.post("/payments/checkout", { tier_id: tier.id, origin_url: window.location.origin, message });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setPaying(null);
    }
  };

  if (streamer === null) return <div className="p-16 text-center text-zinc-500">{t("loading")}</div>;
  if (streamer === false) return <div className="p-16 text-center text-zinc-500">{t("profile_not_found")}</div>;

  const ch = streamer.channel;
  return (
    <div data-testid="streamer-profile-page" className="max-w-5xl mx-auto px-6 py-14">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white">{streamer.name}</h1>
            <BadgeCheck size={26} className="text-emerald-400" />
            {ch.is_live && (
              <span data-testid="profile-live-badge" className="live-pulse inline-flex items-center gap-1.5 bg-red-500 text-white text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                <Radio size={12} /> {t("live")}
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-400 mt-3 capitalize">{ch.platform} · @{ch.handle}</p>
          <div className="flex gap-6 mt-4 text-sm text-zinc-400">
            <span className="flex items-center gap-1.5"><Users size={15} /> {fmtNum(ch.followers)} {t("profile_followers")}</span>
            <span className="flex items-center gap-1.5"><Video size={15} /> {fmtNum(ch.videos)} {t("profile_videos")}</span>
          </div>
        </div>
      </div>

      <div className="bg-amber-950/20 border border-amber-800/40 rounded-2xl p-6 mb-10 flex items-start gap-4">
        <Lock size={20} className="text-amber-400 mt-0.5 shrink-0" />
        <p className="text-sm text-amber-200/80 leading-relaxed">
          <span className="font-semibold text-amber-300">{t("profile_protected")}</span> {t("profile_protected_desc", { s: 100 - fee, p: fee })}
        </p>
      </div>

      {queue.length > 0 && (
        <div data-testid="public-mic-queue" className="bg-zinc-900 border border-emerald-900/40 rounded-2xl p-8 mb-10">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2 mb-1">
            <Mic size={19} className="text-emerald-400" /> {t("pq_title")}
            <span className="ml-1 text-xs font-bold bg-emerald-500 text-zinc-950 rounded-full px-2.5 py-0.5">{queue.length}</span>
          </h2>
          <p className="text-xs text-zinc-500 mb-6">{t("pq_sub")}</p>
          <div className="space-y-2">
            {queue.slice(0, 8).map((q) => (
              <div key={q.position} data-testid="public-queue-row" className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-sm font-black text-emerald-400 w-7 shrink-0">#{q.position}</span>
                  <p className="text-sm font-semibold text-white truncate">{q.fan_name}</p>
                </div>
                <span className="text-xs text-zinc-400 shrink-0">{q.tier_name} · <span className="text-emerald-400 font-semibold">{fmtUsd(q.amount_cents)}</span></span>
              </div>
            ))}
          </div>
        </div>
      )}

      <h2 className="text-base md:text-lg font-bold text-zinc-500 uppercase tracking-[0.2em] mb-8">{t("profile_tiers")}</h2>
      {streamer.tiers.length > 0 && (
        <div className="mb-8">
          <label className="block text-sm font-semibold text-zinc-300 mb-2">{t("profile_msg_label")}</label>
          <textarea data-testid="fan-message-input" maxLength={280} rows={2} value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t("profile_msg_ph")}
            className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-4 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none" />
          <p className="text-xs text-zinc-600 mt-1 text-right">{message.length}/280</p>
        </div>
      )}
      {streamer.tiers.length === 0 ? (
        <p className="text-zinc-500">{t("profile_no_tiers")}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {streamer.tiers.map((tier) => (
            <div key={tier.id} data-testid="support-tier-card" className="bg-zinc-900 border border-zinc-800/60 rounded-2xl p-8 flex flex-col hover:border-emerald-700/60 transition-colors">
              <h3 className="text-xl font-semibold text-white">{tier.name}</h3>
              <p className="text-3xl font-black text-emerald-400 mt-3">{fmtUsd(tier.amount_cents)}</p>
              <p className="text-sm text-zinc-400 mt-3 leading-relaxed flex-1">{tier.description || t("profile_default_desc")}</p>
              <Button data-testid="support-tier-btn" disabled={paying === tier.id} onClick={() => support(tier)}
                className="mt-6 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white w-full">
                {paying === tier.id ? t("profile_redirecting") : t("profile_support")}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
